import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Audit Log interface
 */
export interface AuditLog {
  id: string;
  userId?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  oldValues?: any;
  newValues?: any;
  ipAddress?: string | null;
  userAgent?: string | null;
  outletId?: string | null;
  storeId?: string | null;
  metadata?: any;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    username: string;
  } | null;
  // Computed/derived properties used by UI components
  timestamp?: string;
  username?: string;
  userRole?: string;
  outletName?: string;
  changes?: any;
}

/**
 * Audit filter options
 */
export interface AuditFilter {
  outletId?: string;
  outletIds?: string[];
  storeId?: string;
  userId?: string;
  username?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

/**
 * View mode for audit logs
 */
export type AuditViewMode = 'table' | 'timeline';

/**
 * Audit State Interface
 */
export interface AuditState {
  // Logs data
  logs: AuditLog[];
  selectedLogId: string | null;
  selectedLogDetail: AuditLog | null;

  // Pagination
  totalCount: number;
  currentPage: number;
  pageSize: number;

  // Filters
  filters: AuditFilter;

  // UI state
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  viewMode: AuditViewMode;
}

/**
 * Initial state
 */
const initialState: AuditState = {
  logs: [],
  selectedLogId: null,
  selectedLogDetail: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  filters: {
    page: 1,
    limit: 20,
  },
  isLoading: false,
  isLoadingDetail: false,
  error: null,
  viewMode: 'table',
};

/**
 * Audit Slice
 */
export const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    // Logs management
    setLogs: (state, action: PayloadAction<AuditLog[]>) => {
      state.logs = action.payload;
    },

    addLogs: (state, action: PayloadAction<AuditLog[]>) => {
      // Append logs (for infinite scroll or load more)
      const existingIds = new Set(state.logs.map(log => log.id));
      const newLogs = action.payload.filter(log => !existingIds.has(log.id));
      state.logs = [...state.logs, ...newLogs];
    },

    setSelectedLogId: (state, action: PayloadAction<string | null>) => {
      state.selectedLogId = action.payload;
    },

    setSelectedLogDetail: (state, action: PayloadAction<AuditLog | null>) => {
      state.selectedLogDetail = action.payload;
    },

    clearLogs: (state) => {
      state.logs = [];
      state.totalCount = 0;
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.filters.page = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.filters.limit = action.payload;
      // Reset to first page when page size changes
      state.currentPage = 1;
      state.filters.page = 1;
    },

    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    nextPage: (state) => {
      const maxPage = Math.ceil(state.totalCount / state.pageSize);
      if (state.currentPage < maxPage) {
        state.currentPage += 1;
        state.filters.page = state.currentPage;
      }
    },

    prevPage: (state) => {
      if (state.currentPage > 1) {
        state.currentPage -= 1;
        state.filters.page = state.currentPage;
      }
    },

    goToPage: (state, action: PayloadAction<number>) => {
      const maxPage = Math.ceil(state.totalCount / state.pageSize);
      const page = Math.max(1, Math.min(action.payload, maxPage));
      state.currentPage = page;
      state.filters.page = page;
    },

    // Filters
    setFilters: (state, action: PayloadAction<Partial<AuditFilter>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        page: 1, // Reset to first page when filters change
        limit: state.pageSize,
      };
      state.currentPage = 1;
    },

    updateFilter: (state, action: PayloadAction<{ key: keyof AuditFilter; value: any }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value as never;
      // Reset to first page when filter changes
      state.currentPage = 1;
      state.filters.page = 1;
    },

    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: state.pageSize,
      };
      state.currentPage = 1;
    },

    removeFilter: (state, action: PayloadAction<keyof AuditFilter>) => {
      const key = action.payload;
      delete state.filters[key];
    },

    // UI state
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setIsLoadingDetail: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDetail = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setViewMode: (state, action: PayloadAction<AuditViewMode>) => {
      state.viewMode = action.payload;
    },

    // Reset all state
    resetAuditState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // Clear audit state on logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      () => initialState
    );
  },
});

// Export actions
export const {
  setLogs,
  addLogs,
  setSelectedLogId,
  setSelectedLogDetail,
  clearLogs,
  setCurrentPage,
  setPageSize,
  setTotalCount,
  nextPage,
  prevPage,
  goToPage,
  setFilters,
  updateFilter,
  clearFilters,
  removeFilter,
  setIsLoading,
  setIsLoadingDetail,
  setError,
  setViewMode,
  resetAuditState,
} = auditSlice.actions;

// Selectors
export const selectAudit = (state: RootState) => state.audit;
export const selectAuditLogs = (state: RootState) => state.audit.logs;
export const selectSelectedLogId = (state: RootState) => state.audit.selectedLogId;
export const selectSelectedLog = (state: RootState) => {
  if (!state.audit.selectedLogId) return null;
  return state.audit.logs.find(log => log.id === state.audit.selectedLogId) || null;
};
export const selectSelectedLogDetail = (state: RootState) => state.audit.selectedLogDetail;
export const selectAuditFilters = (state: RootState) => state.audit.filters;
export const selectIsLoading = (state: RootState) => state.audit.isLoading;
export const selectIsLoadingDetail = (state: RootState) => state.audit.isLoadingDetail;
export const selectError = (state: RootState) => state.audit.error;
export const selectCurrentPage = (state: RootState) => state.audit.currentPage;
export const selectPageSize = (state: RootState) => state.audit.pageSize;
export const selectTotalCount = (state: RootState) => state.audit.totalCount;
export const selectViewMode = (state: RootState) => state.audit.viewMode;

// Computed selectors
export const selectHasActiveFilters = (state: RootState) => {
  const filters = state.audit.filters;
  const filterKeys = Object.keys(filters).filter(key => key !== 'page' && key !== 'limit');
  return filterKeys.length > 0;
};

export const selectActiveFilterCount = (state: RootState) => {
  const filters = state.audit.filters;
  const filterKeys = Object.keys(filters).filter(key => key !== 'page' && key !== 'limit');
  return filterKeys.length;
};

export const selectTotalPages = (state: RootState) => {
  return Math.ceil(state.audit.totalCount / state.audit.pageSize);
};

export const selectHasNextPage = (state: RootState) => {
  const totalPages = Math.ceil(state.audit.totalCount / state.audit.pageSize);
  return state.audit.currentPage < totalPages;
};

export const selectHasPrevPage = (state: RootState) => {
  return state.audit.currentPage > 1;
};

export const selectPageInfo = (state: RootState) => {
  const totalPages = Math.ceil(state.audit.totalCount / state.audit.pageSize);
  const start = (state.audit.currentPage - 1) * state.audit.pageSize + 1;
  const end = Math.min(state.audit.currentPage * state.audit.pageSize, state.audit.totalCount);

  return {
    currentPage: state.audit.currentPage,
    totalPages,
    pageSize: state.audit.pageSize,
    totalCount: state.audit.totalCount,
    start,
    end,
    hasNextPage: state.audit.currentPage < totalPages,
    hasPrevPage: state.audit.currentPage > 1,
  };
};

export default auditSlice.reducer;
